import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { ActivityIndicator, Modal, View } from 'react-native';
import { dashboardSummaryService, PlateGroup } from '../../../services/central-api/dashboard-summary';
import { useForm } from 'react-hook-form';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { useQuery } from '@tanstack/react-query';
import { WindowInformation } from '../../../services/window-information';
import MyAppText from '../../../components/MyAppText';
import FormInput from '../../../components/FormInput';
import { translate } from '../../../services/translate';
import FormActions from '../../../components/FormActions';
import Toast from 'react-native-toast-message';
import { LPRServiceError } from '../../../services/central-api/license-plate-recognition';
import { useAuth } from '../../../context/Auth';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    selectedPlateGroupId: number | undefined;
    onConfirm: () => void;
    onClose: () => void;
}

export default function PlateGroupModal({ isModalVisible, onConfirm, onClose, selectedPlateGroupId, setModalVisible }: ModalProps) {
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const { user: authenticatedUser } = useAuth();

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const queryResult = useQuery({
        queryKey: ['plateGroupModal', selectedPlateGroupId],
        queryFn: () => dashboardSummaryService.getPlateGroup(selectedPlateGroupId!),
        enabled: !!selectedPlateGroupId && !!authenticatedUser,
    });

    const isEnabled = !!selectedPlateGroupId && !!authenticatedUser;
    const isFetchingInformation = isEnabled && queryResult.isLoading;

    const { data, refetch } = queryResult;

    const defaultEmptyValues: Omit<PlateGroup, 'id' | 'createdAt' | 'activePlates' | 'inactivePlates'> = {
        name: '',
        description: '',
    };

    const form = useForm<Omit<PlateGroup, 'id' | 'createdAt'>>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    const closeModal = () => {
        form.reset(defaultEmptyValues);
        setModalVisible(false);
        onClose();
    };

    async function formSubmit() {
        try {
            setIsLoading(true);
            const data = form.getValues();

            if (selectedPlateGroupId) {
                await dashboardSummaryService.updatePlateGroup({
                    id: selectedPlateGroupId,
                    name: data.name,
                    description: data.description,
                });
            } else {
                await dashboardSummaryService.createPlateGroup({
                    name: data.name,
                    description: data.description,
                });
            }
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            closeModal();
            onConfirm();
        } catch (err) {
            if (err instanceof LPRServiceError) {
                if (err.message === 'PlateGroupAlreadyExists') {
                    return Toast.show({
                        type: 'sentinelxError',
                        text1: translate('plateGroupAlreadyRegistered')
                    });
                }
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
        }
    }


    useEffect(() => {
        if (!authenticatedUser || !data) {
            return;
        }
        form.reset({
            ...data,
        });

    }, [selectedPlateGroupId, data, authenticatedUser]);


    useEffect(() => {
        if (!isModalVisible || !authenticatedUser) {
            return;
        }

        if (selectedPlateGroupId) {
            refetch();
        } else {
            form.reset(defaultEmptyValues);
        }
    }, [selectedPlateGroupId, authenticatedUser, isModalVisible]);

    return (
        <Modal
            transparent
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => closeModal()}
        >
            <View style={styles.container}>
                <View style={styles.formContainer(windowInfo.isMobile)}>
                    {isFetchingInformation
                        ? <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', minHeight: 250 }}>
                            <ActivityIndicator size='large' color={theme.colors.loadingColor} />
                        </View>
                        : <>
                            <View style={{ flexDirection: 'column', rowGap: 8 }}>
                                <MyAppText style={styles.header}>{translate('monitoringGroupRegister')}</MyAppText>
                                <MyAppText style={styles.subheader}>{translate('fillFieldsBelowToFinishGroupRegister')}</MyAppText>
                            </View>
                            <View style={{ flexDirection: 'column', rowGap: 24 }}>
                                <FormInput
                                    label={translate('name')}
                                    required={true}
                                    form={form}
                                    fieldName='name'
                                    labelStyle={styles.inputLabelStyle}
                                />
                                <FormInput
                                    label={translate('description')}
                                    required={false}
                                    form={form}
                                    fieldName='description'
                                    multiline
                                    numberOfLines={3}
                                    labelStyle={styles.inputLabelStyle}
                                />
                            </View>
                            <FormActions
                                viewStyle={{ flex: 1, justifyContent: 'flex-end' }}
                                buttonStyle={{ paddingHorizontal: 16, paddingVertical: 9, }}
                                buttonContentContainerStyle={{ minWidth: 100 }}
                                onSubmit={formSubmit}
                                onClose={closeModal}
                                disabled={!form.formState.isValid}
                                isLoading={isLoading}
                            />
                        </>
                    }
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
        paddingHorizontal: 16,
    },
    formContainer: (isMobile: boolean) => ({
        width: isMobile ? '95%' : 635,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        paddingHorizontal: 16,
        paddingVertical: 24,
        rowGap: 32,
        // height: 500,
    }),
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 14,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
    inputLabelStyle: {
        fontSize: 14,
        fontWeight: '600',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
        marginBottom: 8,
    },
}));
